// 🔧 src/services/event_results.js
// ✅ Purpose: Load and hydrate privacy-safe event result projections for public result pages.
// ✅ Features:
// - Calls the trusted getEventResults Function instead of reading raw ballots
// - Hydrates aggregate entry IDs with public entry display metadata
// - Adapts Standard, Ranked Choice, Points, and Scorecard projections to existing UI shapes
// ✅ Dependencies: Firebase callable Functions client
// ⚠️ Important: This module never accepts or exposes per-ballot records or voter UIDs.

import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const ownerName = (entry = {}) =>
  `${entry.ownerFirst || ""} ${entry.ownerLast || ""}`.trim() ||
  entry.ownerName ||
  entry.owner ||
  "Unknown Owner";

const hydrateRow = (row = {}, entryMap = {}) => {
  const entry = entryMap[row.cid] || {};
  return {
    ...row,
    entry,
    regcnt: entry.regcnt || "",
    description: entry.description || "",
    name: ownerName(entry),
  };
};

const sortCategories = (categories = []) => [...categories].sort((a, b) => {
  const sortA = Number.isFinite(Number(a?.sortOrder)) ? Number(a.sortOrder) : Number(a?.id) || 9999;
  const sortB = Number.isFinite(Number(b?.sortOrder)) ? Number(b.sortOrder) : Number(b?.id) || 9999;
  return sortA !== sortB
    ? sortA - sortB
    : String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" });
});

const buildPointsResults = (points = {}, entryMap = {}, limit = 3) => {
  const addNormalizedScore = (row) => {
    const observations = (points.criteria || []).filter(
      (criterion) => String(criterion.cid) === String(row.cid) &&
        Number(criterion.maxPoints) > 0 &&
        Number(criterion.judgeCount) > 0
    );
    const scales = [...new Set(observations.map((criterion) => Number(criterion.maxPoints)))];
    if (observations.length === 0 || scales.length !== 1) {
      return {
        ...row,
        normalizedAverageScore: null,
        normalizedAverageScoreDisplay: null,
        normalizedMaxPoints: null,
      };
    }
    const observationCount = observations.reduce(
      (sum, criterion) => sum + Number(criterion.judgeCount || 0),
      0
    );
    const scoreSum = observations.reduce(
      (sum, criterion) => sum + Number(criterion.totalScoreSum || 0),
      0
    );
    const normalizedAverageScore = observationCount > 0 ? scoreSum / observationCount : null;
    return {
      ...row,
      normalizedAverageScore,
      normalizedAverageScoreDisplay: normalizedAverageScore?.toFixed(2) || null,
      normalizedMaxPoints: scales[0],
    };
  };
  const hydratedEntries = (points.entries || [])
    .map(addNormalizedScore)
    .map((row) => hydrateRow(row, entryMap));
  const factors = new Map();
  (points.factors || []).forEach((row) => {
    if (!factors.has(row.factorId)) {
      factors.set(row.factorId, {
        factorId: row.factorId,
        factorName: row.factorName,
        maxPoints: row.maxPoints,
        topEntries: [],
      });
    }
    factors.get(row.factorId).topEntries.push(hydrateRow(row, entryMap));
  });
  factors.forEach((factor) => {
    factor.topEntries = factor.topEntries
      .sort((a, b) => b.averageScore - a.averageScore || b.judgeCount - a.judgeCount)
      .slice(0, limit)
      .map((row, index) => ({ ...row, placement: index + 1 }));
  });

  const criterionFactors = new Map();
  (points.criteria || []).forEach((row) => {
    if (!criterionFactors.has(row.factorId)) {
      criterionFactors.set(row.factorId, {
        factorId: row.factorId,
        factorName: row.factorName,
        criteria: new Map(),
      });
    }
    const factor = criterionFactors.get(row.factorId);
    if (!factor.criteria.has(row.criterionId)) {
      factor.criteria.set(row.criterionId, {
        criterionId: row.criterionId,
        criterionName: row.criterionName,
        maxPoints: row.maxPoints,
        entries: [],
      });
    }
    factor.criteria.get(row.criterionId).entries.push(hydrateRow(row, entryMap));
  });
  const criterionResults = [...criterionFactors.values()].map((factor) => ({
    ...factor,
    criteria: [...factor.criteria.values()],
  }));

  return {
    totalScorecards: points.totalScorecards || 0,
    overallTopEntries: hydratedEntries.slice(0, limit),
    allEntries: hydratedEntries,
    factorLeaderboards: [...factors.values()],
    criterionResults,
  };
};

export const loadEventResultProjection = async ({
  eventId,
  categories = [],
  entryMap = {},
  leaderboardLimit = 3,
} = {}) => {
  const callable = httpsCallable(functions, "getEventResults");
  const response = await callable({ eventId });
  const projection = response.data || {};

  if (projection.votingType === "ranked_choice") {
    const byCategory = new Map(
      (projection.ranked?.categories || []).map((result) => [String(result.catid), result])
    );
    const rankedChoiceResults = sortCategories(categories)
      .filter((category) => String(category.id) !== "0")
      .map((category) => {
        const result = byCategory.get(String(category.id)) || {};
        const finalStandings = (result.finalStandings || []).map((row) => hydrateRow(row, entryMap));
        const leaderboardPlacements = (result.leaderboardPlacements || []).map((row) => {
          const hydrated = hydrateRow(row, entryMap);
          return {
            ...hydrated,
            count: finalStandings.find((standing) => standing.cid === row.cid)?.count || 0,
          };
        });
        return {
          category: category.name,
          results: {
            ...result,
            winnerCar: result.winnerCid ? hydrateRow({ cid: result.winnerCid }, entryMap) : null,
            finalStandings,
            leaderboardPlacements,
          },
        };
      });
    return { projection, rankedChoiceResults, pointsResults: null, carsWithVotes: [], bestInShowCars: [] };
  }

  if (["points", "scorecard"].includes(projection.votingType)) {
    return {
      projection,
      rankedChoiceResults: [],
      pointsResults: buildPointsResults(projection.points, entryMap, leaderboardLimit),
      carsWithVotes: [],
      bestInShowCars: [],
    };
  }

  const standardRows = projection.standard?.entries || [];
  const carsWithVotes = sortCategories(categories)
    .filter((category) => String(category.id) !== "0")
    .map((category) => ({
      category: category.name,
      cars: standardRows
        .filter((row) => String(row.catid) === String(category.id))
        .sort((a, b) => b.count - a.count)
        .slice(0, leaderboardLimit)
        .map((row) => hydrateRow(row, entryMap)),
    }));
  const bestInShowCars = (projection.standard?.bestInShow || [])
    .sort((a, b) => b.count - a.count)
    .slice(0, leaderboardLimit)
    .map((row) => hydrateRow(row, entryMap));

  return { projection, rankedChoiceResults: [], pointsResults: null, carsWithVotes, bestInShowCars };
};
